import { DropDownButton, DropDownButtonItem, DropDownButtonItemClickEvent } from '@progress/kendo-react-buttons';
import React from 'react';
import { RoutePath } from '../../global/enums/RoutePath';
import { gearIcon, gearsIcon, logoutIcon, infoSolidIcon, globeIcon, paletteIcon } from '@progress/kendo-svg-icons';
import { AboutDialog } from './AboutDialog';
import { useGlobalizationContext } from '../../context/globalization/GlobalizationContext';
import { GearLanguage } from '../../context/globalization/GlobalizationProvider';
import { GearLocale } from '../../Model/GearLocale';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../context/globalization/useTranslation';
import { useTheme } from '../../context/theme/ThemeContext';
import { THEME } from '../../config/consts';

const SettingsButton: React.FC = () => {
  const navigate = useNavigate();
  const [isAboutDialogOpen, setIsAboutDialogOpen] = React.useState(false);
  const { selectedLocale, setSelectedLocale, availableLocales } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { theme, toggleTheme } = useTheme();

  const settingsText = translate('custom.settings.title');
  const logoutText = translate('custom.settings.logout');
  const aboutText = translate('custom.settings.about');
  const languageText = translate('custom.topBar.language.title');

  const themeText = translate('custom.settings.theme');
  const lightModeText = translate('custom.settings.lightMode');
  const darkModeText = translate('custom.settings.darkMode');

  const languagesDictionary: GearLanguage[] | undefined = availableLocales?.map(availableLocale => ({
    locale: availableLocale.name as GearLocale,
    text: translate(`custom.topBar.language.${availableLocale.name}`)
  }));

  const handleItemClicked = (event: DropDownButtonItemClickEvent) => {
    switch (event.item.text) {
      case settingsText:
        navigate(`/${RoutePath.SETTINGS}/${RoutePath.SETTINGS_VIEWS}`);
        break;
      case logoutText:
        navigate(RoutePath.LOGOUT);
        break;
      case aboutText:
        setIsAboutDialogOpen(true);
        break;
      case lightModeText:
      case darkModeText:
        toggleTheme();
        break;
      default: {
        const selectedLanguage = languagesDictionary?.find(lang => lang.text === event.item.text);
        if (setSelectedLocale && selectedLanguage) {
          setSelectedLocale(selectedLanguage.locale);
        }
        break;
      }
    }
  };

  const dropdownItems = React.useMemo(() => {
    const items = [<DropDownButtonItem key="settings" svgIcon={gearsIcon} text={settingsText} />];

    items.push(<DropDownButtonItem key="theme-header" disabled={true} svgIcon={paletteIcon} text={themeText} />);
    items.push(
      <DropDownButtonItem
        key="light-mode"
        text={lightModeText}
        selected={theme === THEME.LIGHT}
        disabled={theme === THEME.LIGHT}
      />
    );
    items.push(
      <DropDownButtonItem
        key="dark-mode"
        text={darkModeText}
        selected={theme === THEME.DARK}
        disabled={theme === THEME.DARK}
      />
    );

    if (languagesDictionary && languagesDictionary.length > 0) {
      items.push(<DropDownButtonItem key="lang-header" disabled={true} svgIcon={globeIcon} text={languageText} />);

      languagesDictionary.forEach((language, index) => {
        items.push(
          <DropDownButtonItem
            key={`lang-${index}`}
            text={language.text}
            icon={language.icon}
            selected={language.locale === selectedLocale}
          />
        );
      });
    }

    items.push(<DropDownButtonItem key="about" svgIcon={infoSolidIcon} text={aboutText} />);
    items.push(<DropDownButtonItem key="logout" svgIcon={logoutIcon} text={logoutText} />);

    return items;
  }, [
    languagesDictionary,
    selectedLocale,
    settingsText,
    aboutText,
    logoutText,
    languageText,
    theme,
    themeText,
    lightModeText,
    darkModeText
  ]);

  return (
    <>
      <DropDownButton
        svgIcon={gearIcon}
        onItemClick={handleItemClicked}
        fillMode={'solid'}
        size={'medium'}
        themeColor={'primary'}
      >
        {dropdownItems}
      </DropDownButton>

      <AboutDialog isOpen={isAboutDialogOpen} onClose={() => setIsAboutDialogOpen(false)} />
    </>
  );
};

export default SettingsButton;
